import { appStorage } from './betaStorage'
export type FundingPeriodStatus = 'released' | 'current' | 'upcoming'
export type FundingCadenceMonths = 3 | 6 | 12 | 'custom'

export interface FundingPeriod {
  id: string
  startDate: string
  endDate: string
  amount: number
  rollover: number
  spent: number
}

export interface FundingCategory {
  id: string
  label: string
  allocated: number
  spent: number
  cadenceMonths: FundingCadenceMonths
  periods: FundingPeriod[]
}

export interface ServiceBudget {
  userId: string
  discipline: string
  allocated: number
  spent: number
  estimatedSessionCost: number
  frequency: string
  cadenceMonths: FundingCadenceMonths
  periods: FundingPeriod[]
}

export interface FundingPlan {
  startDate: string
  endDate: string
  categories: FundingCategory[]
}

export interface StoredFunding {
  plan: FundingPlan
  serviceBudgets: ServiceBudget[]
}

const STORAGE_KEY = 'marnixia.funding.v2'
const FUNDING_CHANGED_EVENT = 'marnixia-funding-changed'
const LEO_STORAGE_KEY = 'marnixia.leo.funding.v1'
const LEO_FUNDING_CHANGED_EVENT = 'marnixia-leo-funding-changed'
const LIZZIE_STORAGE_KEY = 'marnixia.lizzie.funding.v1'
const LIZZIE_FUNDING_CHANGED_EVENT = 'marnixia-lizzie-funding-changed'

export const defaultPlan: FundingPlan = {
  startDate: '2026-07-01',
  endDate: '2027-06-30',
  categories: [
    {
      id: 'core', label: 'Core Supports', allocated: 98000, spent: 21450, cadenceMonths: 3,
      periods: [
        { id: 'core-1', startDate: '2026-07-01', endDate: '2026-09-30', amount: 24500, rollover: 0, spent: 21450 },
        { id: 'core-2', startDate: '2026-10-01', endDate: '2026-12-31', amount: 24500, rollover: 3050, spent: 0 },
        { id: 'core-3', startDate: '2027-01-01', endDate: '2027-03-31', amount: 24500, rollover: 0, spent: 0 },
        { id: 'core-4', startDate: '2027-04-01', endDate: '2027-06-30', amount: 24500, rollover: 0, spent: 0 },
      ],
    },
    {
      id: 'capacity', label: 'Capacity Building', allocated: 58450, spent: 13280, cadenceMonths: 3,
      periods: [
        { id: 'capacity-1', startDate: '2026-07-01', endDate: '2026-09-30', amount: 14612.5, rollover: 0, spent: 13280 },
        { id: 'capacity-2', startDate: '2026-10-01', endDate: '2026-12-31', amount: 14612.5, rollover: 1332.5, spent: 0 },
        { id: 'capacity-3', startDate: '2027-01-01', endDate: '2027-03-31', amount: 14612.5, rollover: 0, spent: 0 },
        { id: 'capacity-4', startDate: '2027-04-01', endDate: '2027-06-30', amount: 14612.5, rollover: 0, spent: 0 },
      ],
    },
    {
      id: 'capital', label: 'Capital Supports', allocated: 26000, spent: 4200, cadenceMonths: 12,
      periods: [{ id: 'capital-1', startDate: '2026-07-01', endDate: '2027-06-30', amount: 26000, rollover: 0, spent: 4200 }],
    },
  ],
}

export const defaultServiceBudgets: ServiceBudget[] = [
  { userId: 'u-amy', discipline: 'Occupational Therapy', allocated: 16000, spent: 4200, estimatedSessionCost: 194, frequency: 'Fortnightly', cadenceMonths: 3, periods: [
    { id: 'ot-1', startDate: '2026-07-01', endDate: '2026-09-30', amount: 4000, rollover: 0, spent: 4200 },
    { id: 'ot-2', startDate: '2026-10-01', endDate: '2026-12-31', amount: 4000, rollover: 0, spent: 0 },
    { id: 'ot-3', startDate: '2027-01-01', endDate: '2027-03-31', amount: 4000, rollover: 0, spent: 0 },
    { id: 'ot-4', startDate: '2027-04-01', endDate: '2027-06-30', amount: 4000, rollover: 0, spent: 0 },
  ] },
  { userId: 'u-sarah', discipline: 'Psychology', allocated: 12000, spent: 2800, estimatedSessionCost: 233, frequency: 'Fortnightly', cadenceMonths: 3, periods: [
    { id: 'psych-1', startDate: '2026-07-01', endDate: '2026-09-30', amount: 3000, rollover: 0, spent: 2800 },
    { id: 'psych-2', startDate: '2026-10-01', endDate: '2026-12-31', amount: 3000, rollover: 200, spent: 0 },
    { id: 'psych-3', startDate: '2027-01-01', endDate: '2027-03-31', amount: 3000, rollover: 0, spent: 0 },
    { id: 'psych-4', startDate: '2027-04-01', endDate: '2027-06-30', amount: 3000, rollover: 0, spent: 0 },
  ] },
  { userId: 'u-mia', discipline: 'Speech Pathology', allocated: 14000, spent: 3100, estimatedSessionCost: 194, frequency: 'Weekly', cadenceMonths: 3, periods: [
    { id: 'speech-1', startDate: '2026-07-01', endDate: '2026-09-30', amount: 3500, rollover: 0, spent: 3100 },
    { id: 'speech-2', startDate: '2026-10-01', endDate: '2026-12-31', amount: 3500, rollover: 400, spent: 0 },
    { id: 'speech-3', startDate: '2027-01-01', endDate: '2027-03-31', amount: 3500, rollover: 0, spent: 0 },
    { id: 'speech-4', startDate: '2027-04-01', endDate: '2027-06-30', amount: 3500, rollover: 0, spent: 0 },
  ] },
  { userId: 'u-jordan', discipline: 'Behaviour Support', allocated: 18000, spent: 5000, estimatedSessionCost: 215, frequency: 'Fortnightly', cadenceMonths: 3, periods: [
    { id: 'bsp-1', startDate: '2026-07-01', endDate: '2026-09-30', amount: 4500, rollover: 0, spent: 5000 },
    { id: 'bsp-2', startDate: '2026-10-01', endDate: '2026-12-31', amount: 4500, rollover: 0, spent: 0 },
    { id: 'bsp-3', startDate: '2027-01-01', endDate: '2027-03-31', amount: 4500, rollover: 0, spent: 0 },
    { id: 'bsp-4', startDate: '2027-04-01', endDate: '2027-06-30', amount: 4500, rollover: 0, spent: 0 },
  ] },
]

function leoQuarterlyPeriods(prefix: string, allocated: number, spent: number): FundingPeriod[] {
  const amount = allocated / 4
  return [
    { id: `${prefix}-1`, startDate: '2026-07-01', endDate: '2026-09-30', amount, rollover: 0, spent },
    { id: `${prefix}-2`, startDate: '2026-10-01', endDate: '2026-12-31', amount, rollover: Math.max(0, amount - spent), spent: 0 },
    { id: `${prefix}-3`, startDate: '2027-01-01', endDate: '2027-03-31', amount, rollover: 0, spent: 0 },
    { id: `${prefix}-4`, startDate: '2027-04-01', endDate: '2027-06-30', amount, rollover: 0, spent: 0 },
  ]
}

export const defaultLeoPlan: FundingPlan = {
  startDate: '2026-07-01',
  endDate: '2027-06-30',
  categories: [
    { id: 'leo-core', label: 'Core — Assistance with Daily Life', allocated: 18400, spent: 6920, cadenceMonths: 3, periods: leoQuarterlyPeriods('leo-core', 18400, 6920) },
    { id: 'leo-capacity', label: 'Capacity Building — Improved Daily Living', allocated: 9600, spent: 3150, cadenceMonths: 3, periods: leoQuarterlyPeriods('leo-capacity', 9600, 3150) },
    { id: 'leo-behaviour', label: 'Behaviour Support', allocated: 5200, spent: 1520, cadenceMonths: 3, periods: leoQuarterlyPeriods('leo-behaviour', 5200, 1520) },
  ],
}

export const defaultLeoServiceBudgets: ServiceBudget[] = [
  { userId: 'u-mia', discipline: 'Speech Pathology', allocated: 3600, spent: 1050, estimatedSessionCost: 194, frequency: 'Fortnightly', cadenceMonths: 3, periods: leoQuarterlyPeriods('leo-speech', 3600, 1050) },
  { userId: 'u-jordan', discipline: 'Behaviour Support', allocated: 5200, spent: 1520, estimatedSessionCost: 215, frequency: 'Fortnightly', cadenceMonths: 3, periods: leoQuarterlyPeriods('leo-behaviour-service', 5200, 1520) },
  { userId: 'u-olivia', discipline: 'Occupational Therapy', allocated: 4800, spent: 1200, estimatedSessionCost: 194, frequency: 'Fortnightly', cadenceMonths: 3, periods: leoQuarterlyPeriods('leo-ot', 4800, 1200) },
]

export const defaultLizziePlan: FundingPlan = {
  startDate: '2026-07-01',
  endDate: '2027-06-30',
  categories: [
    { id: 'lizzie-core', label: 'Core — Assistance with Daily Life', allocated: 14200, spent: 2840, cadenceMonths: 3, periods: leoQuarterlyPeriods('lizzie-core', 14200, 2840) },
    { id: 'lizzie-capacity', label: 'Capacity Building — Early Childhood Supports', allocated: 11200, spent: 2360, cadenceMonths: 3, periods: leoQuarterlyPeriods('lizzie-capacity', 11200, 2360) },
    { id: 'lizzie-social', label: 'Social and Community Participation', allocated: 6400, spent: 960, cadenceMonths: 3, periods: leoQuarterlyPeriods('lizzie-social', 6400, 960) },
  ],
}

export const defaultLizzieServiceBudgets: ServiceBudget[] = [
  { userId: 'u-mia', discipline: 'Speech Pathology', allocated: 3200, spent: 880, estimatedSessionCost: 194, frequency: 'Fortnightly', cadenceMonths: 3, periods: leoQuarterlyPeriods('lizzie-speech', 3200, 880) },
  { userId: 'u-jordan', discipline: 'Behaviour Support', allocated: 2600, spent: 520, estimatedSessionCost: 215, frequency: 'Monthly', cadenceMonths: 3, periods: leoQuarterlyPeriods('lizzie-behaviour', 2600, 520) },
  { userId: 'u-olivia', discipline: 'Occupational Therapy', allocated: 4200, spent: 980, estimatedSessionCost: 194, frequency: 'Fortnightly', cadenceMonths: 3, periods: leoQuarterlyPeriods('lizzie-ot', 4200, 980) },
]

export function isISODate(value: string) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(new Date(`${value}T00:00:00`).getTime())
}

export function isValidDateRange(startDate: string, endDate: string) {
  return isISODate(startDate) && isISODate(endDate) && startDate <= endDate
}

export function validateFundingPeriods(periods: FundingPeriod[]) {
  const sorted = periods.slice().sort((a, b) => a.startDate.localeCompare(b.startDate))
  for (let i = 0; i < sorted.length; i += 1) {
    const period = sorted[i]
    if (!isValidDateRange(period.startDate, period.endDate)) return 'A funding period has a finish date before its start date.'
    const previous = sorted[i - 1]
    if (previous && period.startDate <= previous.endDate) return 'Funding periods overlap. Adjust the start or finish dates.'
  }
  return ''
}

export function loadFunding(): StoredFunding {
  try {
    const raw = appStorage.getItem(STORAGE_KEY)
    if (!raw) return { plan: defaultPlan, serviceBudgets: defaultServiceBudgets }
    const parsed = JSON.parse(raw) as Partial<StoredFunding>
    return { plan: parsed.plan ?? defaultPlan, serviceBudgets: parsed.serviceBudgets ?? defaultServiceBudgets }
  } catch {
    return { plan: defaultPlan, serviceBudgets: defaultServiceBudgets }
  }
}

export function saveFunding(value: StoredFunding) {
  appStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  window.dispatchEvent(new Event(FUNDING_CHANGED_EVENT))
}

export function loadLeoFunding(): StoredFunding {
  try {
    const raw = appStorage.getItem(LEO_STORAGE_KEY)
    if (!raw) return { plan: defaultLeoPlan, serviceBudgets: defaultLeoServiceBudgets }
    const parsed = JSON.parse(raw) as Partial<StoredFunding>
    return { plan: parsed.plan ?? defaultLeoPlan, serviceBudgets: parsed.serviceBudgets ?? defaultLeoServiceBudgets }
  } catch {
    return { plan: defaultLeoPlan, serviceBudgets: defaultLeoServiceBudgets }
  }
}

export function saveLeoFunding(value: StoredFunding) {
  appStorage.setItem(LEO_STORAGE_KEY, JSON.stringify(value))
  window.dispatchEvent(new Event(LEO_FUNDING_CHANGED_EVENT))
}

export function loadLizzieFunding(): StoredFunding {
  try {
    const raw = appStorage.getItem(LIZZIE_STORAGE_KEY)
    if (!raw) return { plan: defaultLizziePlan, serviceBudgets: defaultLizzieServiceBudgets }
    const parsed = JSON.parse(raw) as Partial<StoredFunding>
    return { plan: parsed.plan ?? defaultLizziePlan, serviceBudgets: parsed.serviceBudgets ?? defaultLizzieServiceBudgets }
  } catch {
    return { plan: defaultLizziePlan, serviceBudgets: defaultLizzieServiceBudgets }
  }
}

export function saveLizzieFunding(value: StoredFunding) {
  appStorage.setItem(LIZZIE_STORAGE_KEY, JSON.stringify(value))
  window.dispatchEvent(new Event(LIZZIE_FUNDING_CHANGED_EVENT))
}

function daysInMonth(year: number, monthIndex: number) {
  return new Date(year, monthIndex + 1, 0).getDate()
}

function addMonths(dateISO: string, months: number) {
  const [year, month, day] = dateISO.split('-').map(Number)
  const rawMonth = month - 1 + months
  const targetYear = year + Math.floor(rawMonth / 12)
  const targetMonth = ((rawMonth % 12) + 12) % 12
  const targetDay = Math.min(day, daysInMonth(targetYear, targetMonth))
  return `${targetYear}-${String(targetMonth + 1).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`
}

function dayBefore(dateISO: string) {
  const d = new Date(`${dateISO}T00:00:00`)
  d.setDate(d.getDate() - 1)
  return d.toISOString().slice(0, 10)
}

export function generateFundingPeriods(prefix: string, startDate: string, endDate: string, allocated: number, cadenceMonths: 3 | 6 | 12, existing: FundingPeriod[] = []): FundingPeriod[] {
  if (!isValidDateRange(startDate, endDate)) return existing
  const ranges: { startDate: string; endDate: string }[] = []
  let cursor = startDate
  while (cursor <= endDate && ranges.length < 24) {
    const nextStart = addMonths(cursor, cadenceMonths)
    const periodEnd = nextStart > endDate ? endDate : dayBefore(nextStart)
    ranges.push({ startDate: cursor, endDate: periodEnd })
    if (periodEnd === endDate) break
    cursor = nextStart
  }
  const amount = ranges.length ? allocated / ranges.length : allocated
  return ranges.map((range, index) => {
    const prior = existing[index]
    return { id: prior?.id ?? `${prefix}-${index + 1}`, ...range, amount, rollover: prior?.rollover ?? 0, spent: prior?.spent ?? 0 }
  })
}

export function realignFundingToPlanDates(value: StoredFunding, startDate: string, endDate: string): StoredFunding {
  if (!isValidDateRange(startDate, endDate)) return value
  const plan = {
    ...value.plan,
    startDate,
    endDate,
    categories: value.plan.categories.map((category) => category.cadenceMonths === 'custom' ? category : {
      ...category,
      periods: generateFundingPeriods(category.id, startDate, endDate, category.allocated, category.cadenceMonths, category.periods),
    }),
  }
  const serviceBudgets = value.serviceBudgets.map((budget) => budget.cadenceMonths === 'custom' ? budget : {
    ...budget,
    periods: generateFundingPeriods(`svc-${budget.userId}`, startDate, endDate, budget.allocated, budget.cadenceMonths, budget.periods),
  })
  return { plan, serviceBudgets }
}

export function money(value: number) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(Number.isFinite(value) ? value : 0)
}

export function fundingDate(dateISO: string) {
  if (!isISODate(dateISO)) return 'Invalid date'
  return new Date(`${dateISO}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function periodStatus(period: FundingPeriod, todayISO: string): FundingPeriodStatus {
  if (!isValidDateRange(period.startDate, period.endDate)) return 'upcoming'
  if (period.endDate < todayISO) return 'released'
  if (period.startDate <= todayISO && period.endDate >= todayISO) return 'current'
  return 'upcoming'
}

export function availableInPeriod(period: FundingPeriod) {
  return Math.max(0, period.amount + period.rollover - period.spent)
}

export function releasedAmount(periods: FundingPeriod[], todayISO: string) {
  return periods.filter((period) => isValidDateRange(period.startDate, period.endDate) && period.startDate <= todayISO).reduce((sum, period) => sum + period.amount, 0)
}

export function currentFundingPeriod(periods: FundingPeriod[], todayISO: string) {
  return periods.find((period) => isValidDateRange(period.startDate, period.endDate) && period.startDate <= todayISO && period.endDate >= todayISO)
}

export function nextFundingPeriod(periods: FundingPeriod[], todayISO: string) {
  return periods.filter((period) => isValidDateRange(period.startDate, period.endDate) && period.startDate > todayISO).sort((a, b) => a.startDate.localeCompare(b.startDate))[0]
}

export function planTotals(plan: FundingPlan, todayISO = new Date().toISOString().slice(0, 10)) {
  const allocated = plan.categories.reduce((sum, item) => sum + item.allocated, 0)
  const spent = plan.categories.reduce((sum, item) => sum + item.spent, 0)
  const released = plan.categories.reduce((sum, item) => sum + releasedAmount(item.periods, todayISO), 0)
  const availableNow = Math.max(0, released - spent)
  return { allocated, released, spent, availableNow, remaining: Math.max(0, allocated - spent) }
}

export function serviceBudgetFor(serviceBudgets: ServiceBudget[], userId: string) {
  return serviceBudgets.find((item) => item.userId === userId)
}

